(function () {
  // Quick-view for product cards carrying [data-quick-view-trigger] +
  // [data-quick-view-product-json] — originally built for "Fresh off the
  // cutter" cards (theme/sections/hot-right-now.liquid), now also loaded by
  // main-collection.liquid for the Catalog/Hot Now grids. Entirely
  // delegated/generic (a single document-level click listener, selectors
  // scoped to the closest .product-card), so no per-section wiring is
  // needed beyond emitting the right markup and including this script tag.
  // Deliberately its own modal (not a reuse of ai-search.js's
  // .preview-modal-overlay) — that modal is a singleton keyed by a fixed
  // class name, and built around fields (a Buy button, a per-size dimension
  // readout) that don't apply here: a Hot Right Now/Catalog card is a real
  // product with its own product page, not an unpurchased preview still
  // choosing a size. Reusing its class names would mean the two features
  // fighting over one DOM element and field set. Same "own copy, not a
  // shared base class" precedent as .recent-modal-overlay in theme.css,
  // which exists for the same reason. Its CSS is deliberately styled to
  // match .preview-modal's look (same size, same description treatment)
  // even though the class names differ, so a real product's "preview" and
  // an unpurchased design's preview read as the same feature to a shopper.
  const modalStrings = (window.PieceworkStrings && window.PieceworkStrings.modal) || {};
  const productStrings = (window.PieceworkStrings && window.PieceworkStrings.product) || {};

  // Mirrors src/sizeSpecs.js's SIZE_SPECS width/height, same copy as
  // product-form.js/ai-search.js — used to crop-preview the selected size
  // in the modal image (see selectVariant below).
  const SIZE_DIMENSIONS = {
    110: { horizontal: { width: 10, height: 7.99 }, vertical: { width: 7.99, height: 10 } },
    252: { horizontal: { width: 13.5, height: 10.59 }, vertical: { width: 10.59, height: 13.5 } },
    520: { horizontal: { width: 20, height: 15.98 }, vertical: { width: 15.98, height: 20 } },
    1014: { horizontal: { width: 28.5, height: 19.02 }, vertical: { width: 19.02, height: 28.5 } },
  };

  let modal;

  function formatMoney(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  function buildModal() {
    const overlay = document.createElement("div");
    overlay.className = "quick-view-modal-overlay";
    overlay.innerHTML = `
      <div class="quick-view-modal" role="dialog" aria-modal="true">
        <button type="button" class="quick-view-modal__close" data-quick-view-close aria-label="${modalStrings.close || "Close"}">&times;</button>
        <img class="quick-view-modal__image" data-quick-view-image alt="">
        <div class="quick-view-modal__body">
          <h3 data-quick-view-title></h3>
          <p class="quick-view-modal__description" data-quick-view-description hidden></p>
          <div class="quick-view-modal__sizes" data-quick-view-sizes></div>
          <div class="quick-view-modal__price" data-quick-view-price></div>
          <div class="quick-view-modal__actions">
            <button type="button" class="btn btn-primary quick-view-modal__add-to-cart" data-quick-view-add-to-cart></button>
          </div>
          <a class="quick-view-modal__details-link" data-quick-view-details-link>${modalStrings.viewFullDetails || "View full details"}</a>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) closeModal();
    });
    overlay.querySelector("[data-quick-view-close]").addEventListener("click", closeModal);
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeModal();
    });

    return overlay;
  }

  function closeModal() {
    if (modal) modal.classList.remove("is-open");
  }

  // `cartImageSource` wants a 300px cart-line-item-sized image, separate
  // from the modal's own 1200px display image — this store's variants don't
  // carry their own per-size photos, so one shared cart-image value works
  // for every size pill. Read from the trigger's own `data-quick-view-cart-image`
  // (falls back to the display image if somehow absent).
  //
  // `product` is the full `{{ product | json }}` blob from this card's
  // `[data-quick-view-product-json]` script tag — same technique
  // main-product.liquid/product-form.js use for the real product page's own
  // size selector, reused here so quick-view can switch variant/price by
  // size exactly like the PDP does, not just show the one variant the card
  // happened to resolve server-side.
  //
  // `orientation` ("horizontal"/"vertical"/"") is this product's own
  // `orientation:*` tag (src/createProduct.js tags every product with one),
  // read from `data-quick-view-orientation` — same real print-ratio
  // treatment as ai-search.js's preview modal, so a real product's photo
  // shows in its true shape here too instead of a generic square.
  function openQuickView(triggerData, cartImageSource, product, orientation) {
    if (!modal) modal = buildModal();

    // Same preload-then-swap as ai-search.js's openPreviewModal(): the
    // smaller cart-sized image is already downloaded/cached from the card
    // grid, so show that immediately, then swap up to the larger display
    // image once it's actually loaded — no blank/faded frame while it
    // downloads, and switching between products feels instant either way.
    const imageEl = modal.querySelector("[data-quick-view-image]");
    const fullSrc = triggerData.image || cartImageSource;
    imageEl.dataset.requestedSrc = fullSrc;
    if (cartImageSource) imageEl.src = cartImageSource;
    if (fullSrc && fullSrc !== cartImageSource) {
      const preloaded = new Image();
      preloaded.onload = () => {
        if (imageEl.dataset.requestedSrc === fullSrc) imageEl.src = fullSrc;
      };
      preloaded.src = fullSrc;
    } else {
      imageEl.src = fullSrc;
    }
    imageEl.alt = triggerData.title;
    imageEl.classList.toggle("is-orientation-vertical", orientation === "vertical");
    imageEl.classList.toggle("is-orientation-horizontal", orientation === "horizontal");
    modal.querySelector("[data-quick-view-title]").textContent = triggerData.title;
    modal.querySelector("[data-quick-view-details-link]").href = triggerData.url;

    // Plain-text like ai-search.js's preview-modal description (a merchant's
    // body_html can carry markup; stripped down to one paragraph of text so
    // the two modals read the same rather than one suddenly showing rich
    // HTML the other never does).
    const descriptionEl = modal.querySelector("[data-quick-view-description]");
    const descriptionText = (product && product.description ? product.description : "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (descriptionText) {
      descriptionEl.hidden = false;
      descriptionEl.textContent = descriptionText;
    } else {
      descriptionEl.hidden = true;
      descriptionEl.textContent = "";
    }

    const priceEl = modal.querySelector("[data-quick-view-price]");
    const addToCartButton = modal.querySelector("[data-quick-view-add-to-cart]");
    const sizesEl = modal.querySelector("[data-quick-view-sizes]");
    sizesEl.innerHTML = "";

    // Selecting a variant updates price + which variant "Add to cart"
    // targets. The button itself is left wired for
    // piecework-cart.js's global [data-piecework-add-product] delegated
    // listener — clicking it works for free, "Added ✓" flash included, via
    // that same existing listener, no new cart logic here.
    function selectVariant(variant) {
      priceEl.textContent = formatMoney(variant.price);

      // Overrides the orientation class's static master-size ratio with the
      // selected size's real print ratio, same center-crop-in-CSS technique
      // as ai-search.js's preview modal / product-form.js's PDP image.
      const pieces = parseInt(variant.option1, 10);
      const dims = SIZE_DIMENSIONS[pieces]?.[orientation];
      imageEl.style.aspectRatio = dims ? `${dims.width} / ${dims.height}` : "";

      if (variant.available) {
        addToCartButton.hidden = false;
        addToCartButton.textContent = productStrings.addToCart || "Add to cart";
        addToCartButton.dataset.pieceworkAddProduct = "";
        addToCartButton.dataset.variantId = variant.id;
        addToCartButton.dataset.title = triggerData.title;
        addToCartButton.dataset.price = String(variant.price / 100);
        addToCartButton.dataset.image = cartImageSource;
        addToCartButton.dataset.url = triggerData.url;
        addToCartButton.dataset.pieces = parseInt(variant.option1, 10);
      } else {
        addToCartButton.hidden = true;
      }
    }

    const variants = (product && product.variants) || [];
    if (variants.length > 1) {
      sizesEl.hidden = false;
      const initiallySelected = variants.find((v) => v.available) || variants[0];
      variants.forEach((variant) => {
        const pill = document.createElement("button");
        pill.type = "button";
        pill.className = "size-pill" + (variant.id === initiallySelected.id ? " is-selected" : "");
        // option1 is this store's one and only product option ("Size" —
        // values like "110 piece"); parseInt reads its leading number and
        // ignores the trailing unit word. Price isn't repeated here — it's
        // already shown in .quick-view-modal__price, which updates on
        // selection (see selectVariant below); showing it twice per pill
        // read as cluttered.
        const pieces = parseInt(variant.option1, 10);
        pill.textContent = pieces ? pieces + " pc" : variant.option1;
        if (!variant.available) pill.disabled = true;
        pill.addEventListener("click", () => {
          sizesEl.querySelectorAll(".size-pill").forEach((p) => p.classList.remove("is-selected"));
          pill.classList.add("is-selected");
          selectVariant(variant);
        });
        sizesEl.appendChild(pill);
      });
      selectVariant(initiallySelected);
    } else {
      sizesEl.hidden = true;
      if (variants[0]) selectVariant(variants[0]);
    }

    // Force a reflow before toggling .is-open — same first-open fix as
    // ai-search.js's openPreviewModal()/the cart drawer (buildModal()
    // inserting the element and this adding .is-open in the same tick
    // means the browser never paints the "closed" state as a real frame
    // first, so the scale/fade transition doesn't play on the very first
    // open).
    void modal.offsetWidth;
    modal.classList.add("is-open");
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-quick-view-trigger]");
    if (!trigger) return;
    event.preventDefault();

    const card = trigger.closest(".product-card");
    const productJsonEl = card ? card.querySelector("[data-quick-view-product-json]") : null;
    let product = null;
    try {
      product = productJsonEl ? JSON.parse(productJsonEl.textContent) : null;
    } catch (err) {
      product = null;
    }

    openQuickView(
      {
        title: trigger.dataset.quickViewTitle,
        image: trigger.dataset.quickViewImage,
        url: trigger.dataset.quickViewUrl,
      },
      trigger.dataset.quickViewCartImage || trigger.dataset.quickViewImage,
      product,
      trigger.dataset.quickViewOrientation
    );
  });

  // Exposed so piecework-cart.js (loaded sitewide) can reopen this exact
  // modal for a real-product cart line item — same size pills/Add to
  // cart/View full details behavior as clicking that product's card
  // straight out of Hot Now/Catalog, not a separate cart-only copy.
  window.PieceworkQuickView = { open: openQuickView };
})();
